<script>
	import { getContext } from 'svelte';

	import { keyToken } from '../../globals.js';

	const { getToken } = getContext(keyToken);
	const token = getToken();

	const COLOR_BLACK = '#000000';
	const COLOR_BORDER = '#ccccdd';
	const COLOR_DARK = '#333344';
	const COLOR_LIGHT = '#777788';
	const COLOR_PURPLE = '#8844aa';
	const COLOR_SECONDARY = '#555566';
	const COLOR_WHITE = '#ffffff';

	const ISIN_SMI = 'CH0009980894';

	const HIGHSTOCKS_OPTIONS = {
		chart: {
			backgroundColor: COLOR_DARK,
			height: '56%'
		},
		rangeSelector: {
			buttonTheme: {
				style: {
					color: COLOR_BLACK
				},
				states: {
					hover: {
						fill: COLOR_LIGHT
					},
					select: {
						fill: COLOR_PURPLE,
						style: {
							color: COLOR_WHITE
						}
					}
				}
			},
			inputBoxBorderColor: COLOR_DARK,
			inputStyle: {
				color: COLOR_WHITE
			},
			labelStyle: {
				color: COLOR_LIGHT
			},
			selected: 2
		},
		xAxis: [{
			labels: {
				style: {
					color: COLOR_WHITE
				}
			}
		}],
		responsive: {
			rules: [{
				condition: {
					maxWidth: 1600
				},
				chartOptions: {
					rangeSelector: {
						inputEnabled: false
					}
				}
			}]
		}
	};

	export let stock;
	let compareWithIndex = false;
	let seriesOptions = [];
	let seriesCounter = 0;
	let seriesIsins = [ISIN_SMI];
	let showEvents = true;
	let invalid;
	let stocks = [];
	let portfolioDetails = [];

	$: if (stock != null && stock.isin != null) {
		console.log('Load stock chart data for', stock);
		loadPortfolioDetails();
		updateStockChart();
	}

	function toggleChartType(event) {
		compareWithIndex = !compareWithIndex;
		updateStockChart();

		return event;
	}

	function toggleShowEvents(event) {
		showEvents = !showEvents;
		updateStockChart();

		return event;
	}

	function getTimeOffset() {
		return Math.round((new Date()).getTimezoneOffset() * 60);
	}

	async function loadPortfolioDetails() {
		await fetch('https://ostium.ch/api/borsalino/get_portfolio_details.php', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;charset=utf-8',
				'Authorization': 'JWT ' + encodeURIComponent(token)
			},
			body: JSON.stringify({ isin: stock.isin, historic: true })
		}).then(result => {
			if (result.ok) {
				console.log('Loaded portfolio details successfully.');
				result.json().then(data => {
					console.log('Portfolio details:', data);
					portfolioDetails = data.map(stock => {
						stock.timestamp = new Date(stock.value_date).getTime();
						stock.amount = stock.price / stock.factor;
						stock.comment = (stock.dividend
							? 'Dividend:'
							: (stock.quantity > 0
								? 'Buy'
								: 'Sell') + ` <b>${Math.abs(stock.quantity)}</b> at`)
							+ ` <b>${stock.amount.toFixed(2)}</b>`
							+ (stock.comment != null
								? ` (${stock.comment})`
								: '');
						return stock;
					}).sort((a, b) => a.timestamp - b.timestamp);
				});
			} else {
				console.warn('Could not load portfolio details.', result.statusText);
			}
		});
	}

	function updateStockChart() {
		seriesCounter = 0;
		seriesIsins = [];
		seriesOptions = [];
		stocks = [];
		if (stock.isin === 'WATCHLIST') {
			seriesCounter = 0;
			if (compareWithIndex) {
				seriesIsins.push(ISIN_SMI);
			}
			loadAndRenderWatchlist();
		} else if (compareWithIndex) {
			seriesIsins = [ISIN_SMI, stock.isin];
			renderStockChart(ISIN_SMI);
			renderStockChart(stock.isin);
		} else {
			Highcharts.ajax({
				url: 'https://ostium.ch/api/borsalino/get_prices.php',
				type: 'POST',
				headers: {
					'Authorization': 'JWT ' + encodeURIComponent(token)
				},
				data: { isin: stock.isin, time_offset: getTimeOffset() },
				error: (message) => console.log('Error loading chart data for stock: ' + stock.symbol, message),
				success: plotStockChart
			});
		}
	}

	async function loadAndRenderWatchlist() {
		if (!token) {
			invalid = true;
			console.log('StockChart.loadWatchList: invalid token!', stock, token);
			return;
		}

		await fetch('https://ostium.ch/api/borsalino/get_watchlist.php', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;charset=utf-8',
				'Authorization': 'JWT ' + encodeURIComponent(token)
			}
		}).then(result => {
			if (result.ok) {
				console.log('Loaded watchlist for chart data successfully.');
				invalid = false;
				result.json().then(data => {
					console.log(`Loaded ${data.length} watchlist record${data.length === 1 ? '' : 's'} for chart.`);
					stocks = data.map(stock => {
						seriesIsins.push(stock.isin);
						return { isin: stock.isin, name: stock.name, symbol: stock.symbol, factor: stock.factor };
					});
					seriesIsins.forEach(isin => {
						renderStockChart(isin);
					});
				});
			} else {
				invalid = true;
				console.warn('Could not load watchlist data for chart.', result.statusText);
			}
		});
	}

	function renderStockChart(isin) {
		Highcharts.ajax({
			url: 'https://ostium.ch/api/borsalino/get_prices.php?isin=' + isin,
			type: 'POST',
			headers: {
				'Authorization': 'JWT ' + encodeURIComponent(token)
			},
			data: { isin, time_offset: getTimeOffset() },
			error: (message) => console.log('Error loading chart data for stock ' + isin, message),
			success: successCompare
		});
	}

	function plotStockChart(data) {
		// Preprocess the data into 'OHLC' (open, high, low, close)
		const ohlc = data.map(datum => [
			datum[0] * 1000, // epoch in milliseconds
			datum[1] / stock.factor, // open
			datum[2] / stock.factor, // high
			datum[3] / stock.factor, // low
			datum[4] / stock.factor // close
		]);

		Highcharts.stockChart('chartContainer', {
			...HIGHSTOCKS_OPTIONS,
			yAxis: {
				labels: {
					style: {
						color: COLOR_WHITE
					}
				},
				plotLines: [{
					color: COLOR_BORDER
				}]
			},
			tooltip: {
				backgroundColor: COLOR_SECONDARY,
				shape: 'square',
				headerShape: 'callout',
				borderWidth: 0,
				shadow: false,
				positioner: function (width, height, point) {
					var chart = this.chart,
						position;

					if (point.isHeader) {
						position = {
							x: Math.max(
								// Left side limit
								chart.plotLeft,
								Math.min(
									point.plotX + chart.plotLeft - width / 2,
									// Right side limit
									chart.chartWidth - width - chart.marginRight
								)
							),
							y: point.plotY
						};
					} else {
						position = {
							x: point.series.chart.plotLeft,
							y: point.series.yAxis.top - chart.plotTop
						};
					}

					return position;
				},
				style: {
					color: COLOR_WHITE
				}
			},
			series: [{
				type: 'ohlc',
				id: stock.symbol.toLowerCase().replace('.', '-') + '-ohlc',
				name: stock.symbol + ' Stock Price',
				data: ohlc
			}]
		});
	}

	function plotCompareChart() {
		console.log(`Plotting compare chart for ISINs = [${seriesIsins}]`, seriesOptions);
		Highcharts.stockChart('chartContainer', {
			...HIGHSTOCKS_OPTIONS,
			yAxis: {
				labels: {
					formatter: function () {
						return (this.value > 0 ? '+' : '') + this.value + '%';
					},
					style: {
						color: COLOR_WHITE
					}
				},
				plotLines: [{
					value: 0,
					width: 2,
					color: COLOR_BORDER
				}]
			},
			plotOptions: {
				series: {
					compare: 'percent',
					showInNavigator: true
				}
			},
			tooltip: {
				backgroundColor: COLOR_SECONDARY,
				pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b> ({point.change}%){point.text}<br/>',
				valueDecimals: 2,
				shadow: false,
				split: true,
				style: {
					color: COLOR_WHITE
				}
			},
			responsive: {
				rules: [{
					condition: {
						maxWidth: 1600
					},
					chartOptions: {
						rangeSelector: {
							inputEnabled: false
						}
					}
				}]
			},
			series: seriesOptions
		});
	}

	function successCompare(data) {
		const isin = this.url.match(/isin=([A-Z0-9]+)/)[1].toUpperCase();
		const stockToPlot = stocks.find(s => s.isin === isin) || stock;
		const i = seriesIsins.indexOf(isin);
		seriesOptions[i] = {
			colorIndex: i === 0 ? 0 : i + 1,
			id: `dataseries${i}`,
			name: isin == ISIN_SMI ? 'SMI' : stockToPlot.name,
			data: data.map(datum => [datum[0] * 1000, datum[4] / stockToPlot.factor])
		};

		if (showEvents && seriesIsins.length === 2 && isin !== ISIN_SMI) {
			seriesOptions[2] = {
				type: 'flags',
				colorIndex: i === 0 ? 0 : i + 1,
				name: stockToPlot.name,
				data: portfolioDetails.map(stock => {
					return {
						x: stock.timestamp,
						title: stock.comment.charAt(0),
						text: '<br>' +stock.comment
					};
				}),
				onSeries: `dataseries${i}`,
				shape: 'squarepin',
				width: 16
			}
		}

		// As we're loading the data asynchronously, we don't know what order it
		// will arrive. So we keep a counter and create the chart when all the data is loaded.
		seriesCounter += 1;

		if (seriesCounter === seriesIsins.length) {
			plotCompareChart();
		}
	}
</script>

<div id="chartContainer"></div>

{#if stock != null && stock.isin != null}
<div class="form-check form-check-inline">
	<div class="custom-control custom-switch">
		<input type="checkbox" class="custom-control-input" id="compareWithIndex" checked={compareWithIndex} on:click={toggleChartType}>
		<label class="custom-control-label" for="compareWithIndex">Compare with index</label>
	</div>
	<div class="custom-control custom-switch ml-3">
		<input type="checkbox" class="custom-control-input" id="showEvents" checked={showEvents} disabled={!compareWithIndex} on:click={toggleShowEvents}>
		<label class="custom-control-label" for="showEvents">Show events</label>
	</div>
</div>
{/if}
